import { KycStatus } from '@getstep/proto/common'
import { Feature } from '@getstep/proto/feature'
import { notNullish } from '@getstep/sdk/dist/util/Assert'
import type { NextRouter } from 'next/router'

import { URLS } from '../../components/playbook/urls'
import type { AuthorizedStore } from '../store/authorized/AuthorizedStore'

/**
 * Unified enrollment steps
 */
enum EnrollmentStep {
    EMAIL_VERIFICATION = 'EMAIL_VERIFICATION',
    CARD_DETAILS = 'CARD_DETAILS',
    MEMBERSHIP_CONFIRMATION = 'MEMBERSHIP_CONFIRMATION',
    KYC = 'KYC',
    BANK_LINKING = 'BANK_LINKING',
    BORROWING_APPLICATION = 'BORROWING_APPLICATION',
    BORROW = 'BORROW',
    COMPLETED = 'COMPLETED',
}

/**
 * Old flow step order
 * Flow: Email → Card → Subscription → KYC → Bank → Borrowing App → Borrow
 */
const OLD_FLOW_STEPS: EnrollmentStep[] = [
    EnrollmentStep.EMAIL_VERIFICATION,
    EnrollmentStep.CARD_DETAILS,
    EnrollmentStep.MEMBERSHIP_CONFIRMATION,
    EnrollmentStep.KYC,
    EnrollmentStep.BANK_LINKING,
    EnrollmentStep.BORROWING_APPLICATION,
    EnrollmentStep.BORROW,
]

/**
 * New simplified flow step order
 * Flow: Email → KYC → Bank → Card → Subscription → Borrow (no borrowing app)
 */
const NEW_FLOW_STEPS: EnrollmentStep[] = [
    EnrollmentStep.EMAIL_VERIFICATION,
    EnrollmentStep.KYC,
    EnrollmentStep.BANK_LINKING,
    EnrollmentStep.CARD_DETAILS,
    EnrollmentStep.MEMBERSHIP_CONFIRMATION,
    EnrollmentStep.BORROW,
]

/**
 * State of user completion for enrollment steps
 */
interface EnrollmentState {
    hasEmail: boolean
    hasCard: boolean
    hasSubscription: boolean
    hasCompletedKyc: boolean
    hasBankAccount: boolean
    hasBorrowingApplication: boolean
}

/**
 * Enrollment Flow Manager
 *
 * Centralized manager for handling enrollment flow routing.
 * Determines which step the user should go to next based on:
 * - Their current completion state
 * - The EARLYPAY_SIMPLIFIED_ENROLLMENT feature flag
 *
 * Created once per session and stored on AuthorizedStore.
 *
 * Usage:
 * ```
 * const { enrollmentFlowManager } = authorizedStore
 * await enrollmentFlowManager.navigateToNextStep(router)
 * ```
 */
export class EnrollmentFlowManager {
    private authorizedStore: AuthorizedStore

    constructor(authorizedStore: AuthorizedStore) {
        this.authorizedStore = authorizedStore
    }

    /**
     * Navigate to the next step in the enrollment flow
     */
    async navigateToNextStep(router: NextRouter): Promise<void> {
        const url = this.getNextStepUrl()
        await router.replace(url)
    }

    /**
     * Get the URL for the next step without navigating
     * Useful for debugging or conditional navigation
     */
    getNextStepUrl(): string {
        const state = this.getEnrollmentState()
        const steps = this.isSimplifiedFlowEnabled()
            ? NEW_FLOW_STEPS
            : OLD_FLOW_STEPS
        const nextStep = this.getNextIncompleteStep(state, steps)
        return this.getUrlForStep(nextStep)
    }

    /**
     * Check if user is on simplified enrollment flow
     */
    isUsingSimplifiedFlow(): boolean {
        return this.isSimplifiedFlowEnabled()
    }

    /**
     * Navigate to the previous step in the enrollment flow
     * Based on current URL
     */
    async navigateToPreviousStep(
        router: NextRouter,
        currentUrl: string,
    ): Promise<void> {
        const previousUrl = this.getPreviousStepUrl(currentUrl)
        if (previousUrl) {
            await router.replace(previousUrl)
        }
    }

    /**
     * Get the previous step URL based on current step
     * Returns null if there's no previous step (e.g., at the beginning)
     */
    getPreviousStepUrl(currentUrl: string): string | null {
        // Determine current step from URL
        if (currentUrl.includes(URLS.MEMBERSHIP_CONFIRMATION)) {
            // Both flows: ... -> Card -> Subscription
            return URLS.CARD_DETAILS
        }

        if (currentUrl.includes(URLS.CARD_DETAILS)) {
            if (this.isSimplifiedFlowEnabled()) {
                // New flow: ... -> Bank -> Card
                return URLS.STEP_BLACK_INTRO
            }
            // Old flow: ... -> Email -> Card (or could be subscription management if relinking)
            // For now, don't allow back from card details in old flow
            return null
        }

        if (currentUrl.includes(URLS.KYC_INFO)) {
            if (this.isSimplifiedFlowEnabled()) {
                // New flow: Email -> KYC
                // Don't allow back from KYC
                return null
            }
            // Old flow: Subscription -> KYC
            return URLS.MEMBERSHIP_CONFIRMATION
        }

        if (currentUrl.includes(URLS.STEP_BLACK_INTRO)) {
            return URLS.KYC_INFO
        }

        if (currentUrl.includes(URLS.BORROWING_APPLICATION)) {
            // Old flow only: Bank -> Borrowing App
            return URLS.STEP_BLACK_INTRO
        }

        // No previous step for these screens
        return null
    }

    /**
     * Check if simplified enrollment flow is enabled
     * Checks feature flags on every call to support dynamic flag changes (e.g., in tests)
     * Requires both EARLYPAY_SIMPLIFIED_ENROLLMENT and SEVEN_DAY_TRIAL to be enabled
     */
    private isSimplifiedFlowEnabled(): boolean {
        return (
            this.authorizedStore.featureStore.isEnabled(
                Feature.EARLYPAY_SIMPLIFIED_ENROLLMENT,
            ) &&
            this.authorizedStore.featureStore.isEnabled(Feature.SEVEN_DAY_TRIAL)
        )
    }

    /**
     * Get the current enrollment state based on authorized store
     */
    private getEnrollmentState(): EnrollmentState {
        const hasEmail = notNullish(this.authorizedStore.userStore.emailAlias)

        // Check for card payment method
        const hasCard =
            this.authorizedStore.externalAccountsStore.accounts.some(
                (account: any) =>
                    account.card !== undefined && account.card !== null,
            )

        const hasSubscription = notNullish(
            this.authorizedStore.tierStore.tierSubscription,
        )

        const hasCompletedKyc =
            this.authorizedStore.userStore.user.kycStatus !==
                KycStatus.SKIPPED &&
            this.authorizedStore.userStore.user.kycStatus !== KycStatus.UNKNOWN

        // Check for bank account
        const hasBankAccount =
            this.authorizedStore.externalAccountsStore.accounts.some(
                (account: any) =>
                    account.bank !== undefined && account.bank !== null,
            )

        const hasBorrowingApplication = notNullish(
            this.authorizedStore.borrowingStore.application,
        )

        return {
            hasEmail,
            hasCard,
            hasSubscription,
            hasCompletedKyc,
            hasBankAccount,
            hasBorrowingApplication,
        }
    }

    /**
     * Get the next incomplete step from the ordered steps array
     * Iterates through the steps in order and returns the first incomplete one
     */
    private getNextIncompleteStep(
        state: EnrollmentState,
        steps: EnrollmentStep[],
    ): EnrollmentStep {
        // If user already has borrowing application, skip to borrow
        if (state.hasBorrowingApplication) {
            return EnrollmentStep.BORROW
        }

        // Iterate through steps in order and find first incomplete
        for (const step of steps) {
            if (!this.isStepComplete(step, state)) {
                return step
            }
        }

        // All steps complete
        return EnrollmentStep.BORROW
    }

    /**
     * Check if a specific step is complete
     */
    private isStepComplete(
        step: EnrollmentStep,
        state: EnrollmentState,
    ): boolean {
        switch (step) {
            case EnrollmentStep.EMAIL_VERIFICATION:
                return state.hasEmail
            case EnrollmentStep.CARD_DETAILS:
                return state.hasCard
            case EnrollmentStep.MEMBERSHIP_CONFIRMATION:
                return state.hasSubscription
            case EnrollmentStep.KYC:
                return state.hasCompletedKyc
            case EnrollmentStep.BANK_LINKING:
                return state.hasBankAccount
            case EnrollmentStep.BORROWING_APPLICATION:
                return state.hasBorrowingApplication
            case EnrollmentStep.BORROW:
            case EnrollmentStep.COMPLETED:
                return true
            default:
                return false
        }
    }

    /**
     * Map enrollment step to URL
     */
    private getUrlForStep(step: EnrollmentStep): string {
        switch (step) {
            case EnrollmentStep.EMAIL_VERIFICATION:
                return URLS.EMAIL_VERIFICATION

            case EnrollmentStep.CARD_DETAILS:
                return URLS.CARD_DETAILS

            case EnrollmentStep.MEMBERSHIP_CONFIRMATION:
                return URLS.MEMBERSHIP_CONFIRMATION

            case EnrollmentStep.KYC:
                return URLS.KYC_INFO

            case EnrollmentStep.BANK_LINKING:
                return URLS.STEP_BLACK_INTRO

            case EnrollmentStep.BORROWING_APPLICATION:
                return URLS.BORROWING_APPLICATION

            case EnrollmentStep.BORROW:
                return URLS.BORROW

            case EnrollmentStep.COMPLETED:
                return URLS.BORROW

            default:
                return URLS.EXCLUSIVE_OFFER
        }
    }
}
